/**
 * Centralized Emoji Constants
 *
 * ASCII emoji constants for use across the entire project.
 * Organized by category for easy selection in UI components.
 *
 * @module shared/constants/emojis
 */

// ============================================
// Emoji Categories
// ============================================

/**
 * Emojis organized by category.
 * Each category contains related emojis for specific use cases.
 */
export const EMOJI_CATEGORIES = {
  /** Calendar events, celebrations, scheduling */
  events: ['🎉', '🎂', '🎁', '📅', '⏰', '🔔', '🎊', '🎈', '🪅', '🎀'] as const,

  /** Physical activities, sports, hobbies */
  activities: [
    '🏃',
    '🎮',
    '📚',
    '🎨',
    '🎵',
    '🏠',
    '⚽',
    '🏀',
    '🎾',
    '🚴',
    '🏊',
    '🧘',
    '🎬',
    '🎭',
    '🎪',
  ] as const,

  /** Family members, relationships, pets */
  family: [
    '👨‍👩‍👧‍👦',
    '👶',
    '🐕',
    '🐱',
    '❤️',
    '💕',
    '👨',
    '👩',
    '👧',
    '👦',
    '👴',
    '👵',
    '🏡',
  ] as const,

  /** Status indicators, completion, alerts */
  status: [
    '✅',
    '⏳',
    '❌',
    '⚠️',
    '💡',
    '⭐',
    '🔴',
    '🟡',
    '🟢',
    '🔵',
    '⬜',
    '✨',
  ] as const,

  /** Weather conditions for outdoor planning */
  weather: [
    '☀️',
    '🌧️',
    '❄️',
    '🌈',
    '🌙',
    '⛅',
    '🌤️',
    '🌦️',
    '⛈️',
    '🌊',
  ] as const,

  /** Food, meals, cooking */
  food: [
    '🍕',
    '🎂',
    '🍎',
    '☕',
    '🍳',
    '🥗',
    '🍔',
    '🍝',
    '🥐',
    '🍰',
    '🍿',
    '🥤',
  ] as const,

  /** School, education, learning */
  education: [
    '📚',
    '✏️',
    '📝',
    '🎓',
    '📖',
    '🔬',
    '🧮',
    '🖥️',
    '📐',
    '🎒',
  ] as const,

  /** Health, medical, wellness */
  health: ['🏥', '💊', '🩺', '🦷', '👁️', '💉', '🏋️', '🧘', '😴', '🍏'] as const,

  /** Transportation, travel */
  travel: ['🚗', '✈️', '🚌', '🚆', '🚲', '🛵', '🚕', '⛽', '🗺️', '🧳'] as const,

  /** Household, chores, home tasks */
  household: [
    '🧹',
    '🧺',
    '🧽',
    '🗑️',
    '🛒',
    '🔧',
    '🪴',
    '🛏️',
    '🚿',
    '🧴',
  ] as const,

  /** Money, shopping, finance */
  finance: [
    '💰',
    '💳',
    '🛍️',
    '📊',
    '💸',
    '🏦',
    '📈',
    '🧾',
    '💵',
    '🎯',
  ] as const,

  /** Communication, social */
  social: ['📱', '💬', '📧', '📞', '🤝', '👋', '🎤', '📸', '🎥', '📺'] as const,

  /** Nature, outdoors */
  nature: ['🌳', '🌸', '🌺', '🌻', '🍀', '🌲', '🏔️', '🏖️', '🌅', '🌄'] as const,

  /** Commonly used, quick access */
  common: ['📅', '✅', '⭐', '❤️', '👍', '🎉', '📝', '⏰', '📌', '🔖'] as const,
} as const;

// ============================================
// Type Definitions
// ============================================

/**
 * Available emoji category names
 */
export type EmojiCategory = keyof typeof EMOJI_CATEGORIES;

/**
 * Union type of all emojis in a specific category
 */
export type EmojiInCategory<T extends EmojiCategory> =
  (typeof EMOJI_CATEGORIES)[T][number];

/**
 * Union type of all available emojis
 */
export type Emoji = (typeof EMOJI_CATEGORIES)[EmojiCategory][number];

// ============================================
// Utility Functions & Constants
// ============================================

/**
 * Flat array of all emojis from all categories
 */
export const ALL_EMOJIS: readonly string[] =
  Object.values(EMOJI_CATEGORIES).flat();

/**
 * All available category names
 */
export const ALL_EMOJI_CATEGORIES: readonly EmojiCategory[] = Object.keys(
  EMOJI_CATEGORIES
) as readonly EmojiCategory[];

/**
 * Bulgarian labels for emoji categories
 */
export const EMOJI_CATEGORY_LABELS: Readonly<Record<EmojiCategory, string>> = {
  events: 'Събития',
  activities: 'Дейности',
  family: 'Семейство',
  status: 'Статус',
  weather: 'Време',
  food: 'Храна',
  education: 'Образование',
  health: 'Здраве',
  travel: 'Пътуване',
  household: 'Домакинство',
  finance: 'Финанси',
  social: 'Социални',
  nature: 'Природа',
  common: 'Често използвани',
} as const;

/**
 * Get emojis for a specific category
 */
export function getEmojisForCategory(
  category: EmojiCategory
): readonly string[] {
  return EMOJI_CATEGORIES[category];
}

/**
 * Get multiple categories' emojis combined
 */
export function getEmojisForCategories(
  categories: readonly EmojiCategory[]
): readonly string[] {
  return categories.flatMap((cat) => [...EMOJI_CATEGORIES[cat]]);
}

/**
 * Find which category an emoji belongs to
 */
export function findEmojiCategory(emoji: string): EmojiCategory | null {
  for (const [category, emojis] of Object.entries(EMOJI_CATEGORIES)) {
    if ((emojis as readonly string[]).includes(emoji)) {
      return category as EmojiCategory;
    }
  }
  return null;
}

/**
 * Check if a string is a valid emoji from our set
 */
export function isValidEmoji(value: unknown): value is Emoji {
  return typeof value === 'string' && ALL_EMOJIS.includes(value);
}

// ============================================
// Default Selections
// ============================================

/**
 * Default emojis shown in quick-select mode
 * Most commonly used emojis for quick access
 */
export const DEFAULT_QUICK_EMOJIS: readonly string[] = [
  '📅', // Calendar
  '✅', // Done
  '⭐', // Important
  '❤️', // Love
  '🎉', // Celebration
  '📝', // Note
  '⏰', // Time
  '🏠', // Home
  '👨‍👩‍👧‍👦', // Family
  '🎂', // Birthday
  '🏥', // Medical
  '📚', // School
] as const;

/**
 * Default categories shown in picker
 * Order matters for display
 */
export const DEFAULT_EMOJI_CATEGORIES: readonly EmojiCategory[] = [
  'common',
  'events',
  'activities',
  'family',
  'status',
] as const;
